if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      this.mql = window.matchMedia('(min-width: 750px)');

      this.initGalleryNavigation();

      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    initGalleryNavigation() {
      if (!this.elements.viewer) return;

      const prevBtn = this.elements.viewer.querySelector('button[name="previous"]');
      const nextBtn = this.elements.viewer.querySelector('button[name="next"]');

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.stepMedia('prev');
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.stepMedia('next');
        });
      }

      this.querySelectorAll('.dt-gallery-dot').forEach((dot) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (dot.dataset.target) {
            this.setActiveMedia(dot.dataset.target, false);
          }
        });
      });

      this.initDragSwipe();
      this.initKeyboardNav();
      this.initScrollSync();
    }

    stepMedia(direction) {
      if (!this.elements.viewer) return;
      const slides = Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
      if (slides.length <= 1) return;

      let currentIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (currentIndex === -1) {
        if (this.elements.thumbnails) {
          const activeThumb = this.elements.thumbnails.querySelector('button[aria-current="true"]');
          if (activeThumb) {
            const wrap = activeThumb.closest('[data-target]');
            if (wrap) {
              currentIndex = slides.findIndex((s) => s.dataset.mediaId === wrap.dataset.target);
            }
          }
        }
      }
      if (currentIndex === -1) currentIndex = 0;

      let targetIndex;
      if (direction === 'next') {
        targetIndex = (currentIndex + 1) % slides.length;
      } else {
        targetIndex = (currentIndex - 1 + slides.length) % slides.length;
      }

      const targetSlide = slides[targetIndex];
      if (targetSlide && targetSlide.dataset.mediaId) {
        this.setActiveMedia(targetSlide.dataset.mediaId, false);
      }
    }

    initDragSwipe() {
      const viewer = this.elements.viewer;
      if (!viewer) return;

      let startX = 0;
      let startY = 0;
      let isDown = false;
      let hasDragged = false;
      let startTime = 0;

      viewer.addEventListener('pointerdown', (e) => {
        // Mobile touch uses native horizontal scroll track
        if (e.pointerType === 'touch' && window.innerWidth < 750) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('button, a, input, select, textarea, .slider-buttons')) return;

        startX = e.clientX;
        startY = e.clientY;
        startTime = Date.now();
        isDown = true;
        hasDragged = false;
      }, { passive: true });

      viewer.addEventListener('pointermove', (e) => {
        if (!isDown) return;
        const diffX = e.clientX - startX;
        const diffY = e.clientY - startY;
        if (Math.abs(diffX) > 10 || Math.abs(diffY) > 10) {
          hasDragged = true;
        }
      }, { passive: true });

      const onPointerEnd = (e) => {
        if (!isDown) return;
        isDown = false;

        const diffX = e.clientX - startX;
        const diffY = e.clientY - startY;
        const elapsed = Date.now() - startTime;

        if (Math.abs(diffX) > 35 && Math.abs(diffX) > Math.abs(diffY) * 1.2 && elapsed < 800) {
          if (diffX < 0) {
            this.stepMedia('next');
          } else {
            this.stepMedia('prev');
          }
        }

        setTimeout(() => {
          hasDragged = false;
        }, 150);
      };

      viewer.addEventListener('pointerup', onPointerEnd, { passive: true });
      viewer.addEventListener('pointercancel', onPointerEnd, { passive: true });

      viewer.addEventListener('click', (e) => {
        if (hasDragged) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    }

    initKeyboardNav() {
      window.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const tag = document.activeElement ? document.activeElement.tagName.toLowerCase() : '';
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || (document.activeElement && document.activeElement.isContentEditable)) return;

        const rect = this.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;

        if (e.key === 'ArrowLeft') {
          this.stepMedia('prev');
        } else if (e.key === 'ArrowRight') {
          this.stepMedia('next');
        }
      });
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (activeMedia.parentElement && typeof activeMedia.parentElement.scrollTo === 'function') {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      // Keep counter synced
      const currentCounter = this.elements.viewer.querySelector('.slider-counter--current');
      if (currentCounter) {
        const slides = Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
        const idx = slides.findIndex((s) => s.dataset.mediaId === mediaId);
        if (idx !== -1) {
          currentCounter.textContent = idx + 1;
        }
      }

      // Keep dots synced
      this.querySelectorAll('.dt-gallery-dot').forEach((dot) => {
        const match = dot.dataset.target === mediaId;
        dot.classList.toggle('is-active', match);
        if (match) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) {
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      const btn = thumbnail.querySelector('button');
      if (btn) btn.setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      if (this.elements.thumbnails.slider) {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        if (this.elements.liveRegion) {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = (window.accessibilityStrings && window.accessibilityStrings.imageAvailable)
            ? window.accessibilityStrings.imageAvailable.replace('[index]', position)
            : `Image ${position}`;
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        }
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {      
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    initScrollSync() {
      if (!this.elements.viewer) return;
      const list = this.elements.viewer.querySelector('.product__media-list');
      if (!list) return;

      const slides = Array.from(list.querySelectorAll('.product__media-item'));
      if (slides.length <= 1) return;

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              const targetId = entry.target.dataset.mediaId;
              if (targetId) {
                slides.forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === targetId));
                this.querySelectorAll('.dt-gallery-dot').forEach((dot) => {
                  const match = dot.dataset.target === targetId;
                  dot.classList.toggle('is-active', match);
                  if (match) {
                    dot.setAttribute('aria-current', 'true');
                  } else {
                    dot.removeAttribute('aria-current');
                  }
                });
                const currentCounter = this.elements.viewer.querySelector('.slider-counter--current');
                if (currentCounter) {
                  const idx = slides.findIndex((s) => s.dataset.mediaId === targetId);
                  if (idx !== -1) currentCounter.textContent = idx + 1;
                }
                if (this.elements.thumbnails) {
                  const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${targetId}"]`);
                  if (activeThumbnail) this.setActiveThumbnail(activeThumbnail);
                }
              }
            }
          });
        }, {
          root: list,
          threshold: 0.5
        });

        slides.forEach((slide) => observer.observe(slide));
      }
    }

    removeListSemantic() {
      if (!this.elements.viewer || !this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      if (this.elements.viewer.sliderItems) {
        this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
      }
    }
  });
}
